import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  TextField, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel
} from '@mui/material';
import { toast } from '@/components/toastify';
import emailTemplateHtml from './emailTemplateHtml';

const categories = ['General', 'Onboarding', 'Leave', 'Attendance', 'Payroll'];

const MailTemplate = () => {
  const [templates, setTemplates] = useState([]);
  const [currentTemplate, setCurrentTemplate] = useState({
    id: null,
    name: '',
    subject: '',
    body: '',
    category: '',
    variables: []
  });
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');

  // This would be replaced with actual API calls in a real implementation
  useEffect(() => {
    // Mock data for demonstration
    const mockTemplates = [
      {
        id: 1,
        name: 'Welcome Email',
        subject: 'Welcome to Our Company',
        body: 'Dear {{name}},\n\nWelcome to our company! We are excited to have you join our team.\n\nBest regards,\nHR Team',
        category: 'Onboarding',
        variables: ['name']
      },
      {
        id: 2,
        name: 'Leave Approval',
        subject: 'Leave Request Approved',
        body: 'Dear {{name}},\n\nYour leave request from {{startDate}} to {{endDate}} has been approved.\n\nBest regards,\nHR Team',
        category: 'Leave',
        variables: ['name', 'startDate', 'endDate']
      }
    ];
    setTemplates(mockTemplates);
    if (mockTemplates.length > 0) {
      setSelectedTemplateId(mockTemplates[0].id);
      setCurrentTemplate(mockTemplates[0]);
    }
  }, []);

  useEffect(() => {
    // When selectedTemplateId changes, update currentTemplate
    const found = templates.find(t => t.id === selectedTemplateId);
    if (found) {
      setCurrentTemplate(found);
      generatePreview(found);
    }
  }, [selectedTemplateId, templates]); // eslint-disable-next-line react-hooks/exhaustive-deps

  const handleTemplateDropdownChange = (e) => {
    setSelectedTemplateId(e.target.value);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedTemplate = {
      ...currentTemplate,
      [name]: value
    };
    setCurrentTemplate(updatedTemplate);
    generatePreview(updatedTemplate);
  };

  const generatePreview = (templateArg) => {
    const template = templateArg || currentTemplate;
    try {
      // Use the imported HTML template
      let modifiedHtml = emailTemplateHtml;
      
      // Replace the subject/title
      modifiedHtml = modifiedHtml.replace(
        /<title>Professional Email Template<\/title>/,
        `<title>${template.subject}</title>`
      );
      
      // Replace the welcome section title
      modifiedHtml = modifiedHtml.replace(
        /<h2 class="section-title">Welcome to Our Newsletter<\/h2>/,
        `<h2 class="section-title">${template.subject}</h2>`
      );
      
      // Replace the content with our template body
      let bodyContent = template.body;
      
      // Replace variables with placeholder values
      const regex = /{{([^}]+)}}/g;
      let match;
      while ((match = regex.exec(bodyContent)) !== null) {
        const variable = match[1];
        const placeholder = `[${variable}]`;
        bodyContent = bodyContent.replace(new RegExp(`{{${variable}}}`, 'g'), placeholder);
      }
      
      // Convert line breaks to HTML
      bodyContent = bodyContent.replace(/\n/g, '<br>');
      
      // Insert the body content
      modifiedHtml = modifiedHtml.replace(
        /<div class="section-content">\s*<p><\/p>\s*<p><\/p>\s*<\/div>/,
        `<div class="section-content"><p>${bodyContent}</p></div>`
      );
      
      // Remove the "Latest Updates" section
      modifiedHtml = modifiedHtml.replace(
        /<div class="section">\s*<h2 class="section-title">Latest Updates<\/h2>[\s\S]*?<\/div>\s*<\/div>/,
        ''
      );
      
      setPreviewHtml(modifiedHtml);
    } catch (error) {
      console.error('Error generating email preview:', error);
      toast('error', 'Failed to generate email preview');
      
      // Fallback: create a simple preview with just the content
      const fallbackHtml = `
        <html>
          <head>
            <title>${currentTemplate.subject}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              .header { background-color: #2D6544; color: white; padding: 20px; text-align: center; }
              .content { padding: 20px; }
              .footer { background-color: #2D6544; color: white; padding: 20px; text-align: center; }
            </style>
          </head>
          <body>
            <div class="header">
              <h2>${currentTemplate.subject}</h2>
            </div>
            <div class="content">
              ${currentTemplate.body.replace(/\n/g, '<br>')}
            </div>
            <div class="footer">
              <p>&copy; 2025 Company. All rights reserved.</p>
            </div>
          </body>
        </html>
      `;
      
      setPreviewHtml(fallbackHtml);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Mail Template Management
      </Typography>
    <Paper sx={{ p: 3, mb: 3 }}>
  <Typography variant="h6" gutterBottom>
    Select Template
  </Typography>
  {/* Template Dropdown */}
  <FormControl fullWidth sx={{ maxWidth: 500, mb: 2 }}>
    <InputLabel id="template-select-label">Template</InputLabel>
    <Select
      labelId="template-select-label"
      value={selectedTemplateId}
      label="Template"
      onChange={handleTemplateDropdownChange}
    >
      {templates.map((template) => (
        <MenuItem key={template.id} value={template.id}>
          {template.name}
        </MenuItem>
      ))}
    </Select>
  </FormControl>

  {/* Static Role Dropdown */}
  <FormControl fullWidth sx={{ maxWidth: 500, marginLeft: 3 }}>
    <InputLabel id="role-select-label">Role</InputLabel>
    <Select
      labelId="role-select-label"
      // value={selectedRole}
      label="Role"
      // onChange={handleRoleChange}
    >
      <MenuItem value="Admin">Employee</MenuItem>
      <MenuItem value="Editor">HR Ops Admin</MenuItem>
      <MenuItem value="Viewer">Onboarder</MenuItem>
      <MenuItem value="Viewer">Site Admin</MenuItem>
      <MenuItem value="Viewer">IT Infra</MenuItem>
      <MenuItem value="Viewer">Finance</MenuItem>
      <MenuItem value="Viewer">Graphics Team</MenuItem>
    </Select>
  </FormControl>
</Paper>

      <Grid container spacing={3}>
        {/* Template Editor */}
        <Grid item xs={10} md={5}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Edit Template
            </Typography>
            <Box component="form" sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Template Name"
                name="name"
                value={currentTemplate.name}
                onChange={handleInputChange}
                margin="normal"
                required
              />
              
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Category</InputLabel>
                <Select
                  name="category"
                  value={currentTemplate.category}
                  onChange={handleInputChange}
                  label="Category"
                >
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                label="Subject"
                name="subject"
                value={currentTemplate.subject}
                onChange={handleInputChange}
                margin="normal"
                required
              />
              
              <TextField
                fullWidth
                label="Body"
                name="body"
                value={currentTemplate.body}
                onChange={handleInputChange}
                margin="normal"
                multiline
                rows={10}
                required
                // helperText="Use {{variableName}} for dynamic content"
              />
            </Box>
          </Paper>
        </Grid>
        {/* Email Preview Section - Shows on the right side */}
        <Grid item xs={14} md={7}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Email Preview
            </Typography>
            <Box sx={{ 
              height: 'calc(100% - 40px)', 
              border: '1px solid #e0e0e0', 
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <iframe
                srcDoc={previewHtml}
                title="Email Preview"
                width="100%"
                height="100%"
                style={{ border: 'none' }}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MailTemplate;